import { IParentEntity } from 'app/shared/interfaces/entity';
import { Subscription, take } from 'rxjs';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from '../../../../shared/models/subject';
import { MatDialog } from '@angular/material/dialog';
import { Subject as RxjsSubject } from 'rxjs';
import { UploadDialogComponent } from '../upload-dialog/upload-dialog.component';
import { TaskType } from 'app/shared/models/task-type';
import { AuthService } from 'app/shared/services/auth.service';
import { UrlDialogComponent } from '../url-dialog/url-dialog.component';
import { AttachmentService } from 'app/shared/services/attachment.service';
import { SubjectUrl } from 'app/shared/models/subject-url';
import { Url } from 'app/shared/models/url';
import { FileManagerComponent } from '../file-manager/file-manager.component';
import { Router } from '@angular/router';
import { HyperLawService } from 'app/shared/services/hyper-law.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'attachments',
  templateUrl: './attachments.component.html',
  styleUrls: ['./attachments.component.scss'],
})
export class AttachmentsComponent implements OnInit, OnDestroy {
  title: string;
  attachmentCount = 0;
  fileCount = 0;

  onApplyFilter: Subscription;
  onApplySortOrder: Subscription;
  onChanged: Subscription;
  onSelected: Subscription;
  selectedItems: number[] = [];
  selectAllClicked = new RxjsSubject<void>();
  showCasePrepUI = false;
  onHyperLawConfigValidChanged: Subscription;
  exportToCasePreparationFileClicked = new RxjsSubject<void>();

  appliedSortOrder: any;
  filters: any;

  allowEdit = false;
  userReadOnly = false;

  _parent: IParentEntity;
  @Input() set parent(val: IParentEntity) {
    if (val) {
      this._parent = val;
      if (this._parent.uid && !this.userReadOnly) {
        this.allowEdit = true;
      }
    }
  }
  get parent(): IParentEntity {
    return this._parent;
  }

  @Input() showMailMergeTemplateDownloadButton = false;
  @Input() showUrls = true;
  @Input() alwaysHideCasePrepUI = false;

  @Input() set subject(val: Subject) {
    if (val) {
      const p = val;

      this._parent = {
        id: p.id,
        uid: p.uid,
        type: 'subject',
        summary: p.summary,
        hasCasePreparationFile: p.hasCasePreparationFile,
      };
      this.allowEdit = p.uid && !val.archived && !this.userReadOnly;
    }
  }

  @Input() set taskType(val: TaskType) {
    const p = val;
    this._parent = { id: p.id, uid: p.uid, type: 'taskType', summary: p.name };
    if (p.uid && !this.userReadOnly) {
      this.allowEdit = true;
    }
  }
  @ViewChild('fileInput') fileInput;

  constructor(
    public dialog: MatDialog,
    public attachmentService: AttachmentService,
    public router: Router,
    public authService: AuthService,
    public hyperLawService: HyperLawService
  ) {
    this.title = 'Attachments';
    this.appliedSortOrder = this.attachmentService
      .getSortOrders()
      .find((i) => i.selected);
    this.filters = this.attachmentService.getFilters();
    this.userReadOnly = this.authService.isUserReadOnly();
  }

  ngOnInit() {
    this.onApplySortOrder = this.attachmentService.onApplySortOrder.subscribe((sortOrder) => {
        this.appliedSortOrder = sortOrder;
      }
    );

    this.onApplyFilter = this.attachmentService.onApplyFilter.subscribe((filters) => {
        this.filters = filters;
      }
    );

    this.onSelected = this.attachmentService.selectedFiles.subscribe((items) => {
        this.selectedItems = items && items.length ? items.map((file) => file.id) : [];
    });

    this.onChanged = this.attachmentService.onChanged.subscribe((items: any) => {
      if (items && this._parent) {
        this.attachmentCount = items.filter((item) => !item.isDirectory).length;
        this.fileCount = items.filter((item) => !item.isDirectory && this.attachmentService.isFile(item)).length;
      }
    });

    if (!this.alwaysHideCasePrepUI) {
      this.showCasePrepUI = this.hyperLawService.isConfigValid();
      this.onHyperLawConfigValidChanged = this.hyperLawService.configValid.subscribe((isActive: boolean) => {
        this.showCasePrepUI = isActive;
      });
    }
  }

  ngOnDestroy() {
    if (this.onChanged) this.onChanged.unsubscribe();
    if (this.onApplyFilter) this.onApplyFilter.unsubscribe();
    if (this.onApplySortOrder) this.onApplySortOrder.unsubscribe();
    if (this.onSelected) this.onSelected.unsubscribe();
    if (this.onHyperLawConfigValidChanged) this.onHyperLawConfigValidChanged.unsubscribe();
    if (this.selectAllClicked) this.selectAllClicked.unsubscribe();
    if (this.exportToCasePreparationFileClicked) this.exportToCasePreparationFileClicked.unsubscribe();
  }

  getSortOrderTooltip(): string {
    if (this.appliedSortOrder) {
      return (
        'Sort by ' +
        this.appliedSortOrder.title +
        ' ' +
        (this.appliedSortOrder.order > 0 ? ' (Ascending)' : ' (Descending)')
      );
    }
    return '';
  }

  // Show the upload dialog component
  addFiles() {
    this.dialog
      .open(UploadDialogComponent, {
        data: this.parent,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((_) => {
        this.Update();
      });
  }

  // show the url dialog component
  addUrl() {
    this.dialog
      .open(UrlDialogComponent, {
        data: {
          parent: this.parent,
          subjectUrl: new SubjectUrl({
            subjectId: this.parent.id,
            url: new Url(),
          }),
        },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((_) => {
        this.Update();
      });
  }

  Update() {
    this.attachmentService.getExistingAttachments(this.parent, true, true).pipe(take(1)).subscribe();
  }

  applyFilter(filter) {
    this.attachmentService.applyFilter(filter);
    this.attachmentService.refresh();
  }

  btnApplySortOrder(sortOrder) {
    this.attachmentService.applySortOrder(sortOrder);
    this.attachmentService.refresh();
  }

  btnDownloadMMDataFile() {
    this.attachmentService
      .getMailMergeDataFileDownloadLink()
      .subscribe((url: string) => {
        if (url) {
          window.open(url);
        }
      });
  }

  openFileManager() {
    this.dialog
      .open(FileManagerComponent, {
        data: this.parent,
        disableClose: true,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((_) => this.attachmentService.getAttachments(this.parent));
  }
}
